'use client';

import React, { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import { useAdRotation } from './AdRotationProvider';

interface AdZoneDisplayProps {
  zone: string;
  className?: string;
  currentPage?: string;
  rotationInterval?: number;
  maxAds?: number;
}

export const AdZoneDisplay: React.FC<AdZoneDisplayProps> = ({
  zone,
  className = '',
  currentPage,
  rotationInterval = 30000,
  maxAds = 1
}) => {
  const { getAdsForZone, trackImpression, trackClick } = useAdRotation();
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [availableAds, setAvailableAds] = useState<any[]>([]);

  useEffect(() => {
    const ads = getAdsForZone(zone, currentPage).slice(0, maxAds);
    setAvailableAds(ads);
    setCurrentAdIndex(0);
  }, [zone, currentPage, maxAds, getAdsForZone]);

  useEffect(() => {
    if (availableAds.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentAdIndex((prev) => (prev + 1) % availableAds.length);
    }, rotationInterval);

    return () => clearInterval(interval);
  }, [availableAds.length, rotationInterval]);

  useEffect(() => {
    if (availableAds[currentAdIndex]) {
      const timer = setTimeout(() => {
        trackImpression(availableAds[currentAdIndex].id, zone);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [currentAdIndex, availableAds, trackImpression, zone]);

  if (availableAds.length === 0) {
    return null;
  }

  const currentAd = availableAds[currentAdIndex];

  const handleAdClick = () => {
    trackClick(currentAd.id, zone);
  };

  const getZoneStyles = (zoneName: string) => {
    const styles: Record<string, string> = {
      homepage_leaderboard: 'w-full max-w-[728px] h-24',
      global_leaderboard: 'w-full max-w-[970px] h-24',
      sidebar_mpu: 'w-full max-w-[300px] h-64',
      article_inline_p2: 'w-full max-w-[728px] h-20',
      article_inline_p6: 'w-full max-w-[728px] h-20',
      listing_grid_inline: 'w-full max-w-[300px] h-40',
      job_grid_inline: 'w-full max-w-[300px] h-40',
      footer_banner: 'w-full max-w-[728px] h-16'
    };

    return styles[zoneName] || 'w-full h-32';
  };

  return (
    <div
      className={`${getZoneStyles(zone)} bg-card border border-border rounded-industrial overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-lg ${className}`}
      onClick={handleAdClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && handleAdClick()}
    >
      <div className="w-full h-full relative">
        <div className="absolute top-1 right-1 text-xs text-text-secondary bg-background/80 px-2 py-1 rounded-sm uppercase tracking-wider z-10">
          Ad
        </div>
        
        {currentAd.creative.type === 'banner' && currentAd.creative.image ? (
          <div className="relative w-full h-full">
            <AppImage
              src={currentAd.creative.image}
              alt={currentAd.creative.alt || currentAd.creative.title}
              width={728}
              height={90}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
              <div className="text-center text-white">
                <h3 className="font-heading font-bold text-sm mb-1">
                  {currentAd.creative.title}
                </h3>
                <p className="text-xs mb-2 px-2">
                  {currentAd.creative.description}
                </p>
                <span className="bg-primary text-primary-foreground px-3 py-1 rounded text-xs font-bold">
                  {currentAd.creative.cta}
                </span>
              </div>
            </div>
          </div>
        ) : currentAd.creative.type === 'rich_media' ? (
          <div className="p-4 h-full flex flex-col justify-center">
            {currentAd.creative.image && (
              <div className="mb-3">
                <AppImage
                  src={currentAd.creative.image}
                  alt={currentAd.creative.alt || currentAd.creative.title}
                  width={300}
                  height={120}
                  className="w-full h-20 object-cover rounded"
                />
              </div>
            )}
            <h3 className="font-heading font-bold text-foreground text-sm mb-2">
              {currentAd.creative.title}
            </h3>
            <p className="text-xs text-text-secondary mb-3 leading-relaxed">
              {currentAd.creative.description}
            </p>
            <button className="bg-primary text-primary-foreground px-3 py-2 rounded-industrial text-xs font-heading font-bold uppercase tracking-wide hover:bg-accent transition-colors">
              {currentAd.creative.cta}
            </button>
          </div>
        ) : (
          <div className="p-4 h-full flex flex-col justify-center text-center bg-gradient-to-r from-primary/10 to-accent/10">
            <h3 className="font-heading font-bold text-foreground text-sm mb-2">
              {currentAd.creative.title}
            </h3>
            <p className="text-xs text-text-secondary mb-3 leading-relaxed">
              {currentAd.creative.description}
            </p>
            <span className="bg-primary text-primary-foreground px-3 py-1 rounded text-xs font-bold inline-block">
              {currentAd.creative.cta}
            </span>
          </div>
        )}
        
        {availableAds.length > 1 && (
          <div className="absolute bottom-1 left-1 flex space-x-1">
            {availableAds.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full ${
                  index === currentAdIndex ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdZoneDisplay;